// assets/cart-gift-wrapping.js
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

class CartGiftWrappingComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.refs.giftWrappingCheckbox?.addEventListener('change', this.#onChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.giftWrappingCheckbox?.removeEventListener('change', this.#onChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalCartUpdate);
  }

  /**
   * 他の商品の追加・削除・数量変更後、ギフトラッピング以外の商品が
   * カートからすべてなくなっていたら、自動でギフトラッピングも外す。
   */
  #handleExternalCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const items = detail?.items;
        if (!items) return;

        const hasOtherItems = items.some((item) => `${item.variant_id}` !== `${this.variantId}`);
        const checkbox = this.refs.giftWrappingCheckbox;

        if (!hasOtherItems && checkbox?.checked) {
          checkbox.checked = false;
          this.#autoRemoveGiftWrapping();
        }
      })
      .catch(() => {});
  };

  async #autoRemoveGiftWrapping() {
    this.classList.add('cart-items-disabled');

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: this.variantId, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const { sectionsHtml, cartData } = await this.#removeGiftWrapping();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData),
        detail: {
          sections: sectionsHtml,
          items: cartData.items,
          source: 'cart-gift-wrapping-component',
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to remove gift wrapping',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.classList.remove('cart-items-disabled');
    }
  }

  /** @param {Event} event */
  #onChange = async (event) => {
    const checkbox = /** @type {HTMLInputElement} */ (event.target);
    const checked = checkbox.checked;

    checkbox.disabled = true;
    this.classList.add('cart-items-disabled');

    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: checked ? 'add' : 'remove',
        context: 'cart',
        lines: [{ id: this.variantId, quantity: checked ? 1 : 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const { sectionsHtml, cartData } = checked
        ? await this.#addGiftWrapping()
        : await this.#removeGiftWrapping();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData),
        detail: {
          sections: sectionsHtml,
          items: cartData.items,
          source: 'cart-gift-wrapping-component',
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      checkbox.checked = !checked; // 失敗時は状態を戻す
      deferred.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update gift wrapping',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      checkbox.disabled = false;
      this.classList.remove('cart-items-disabled');
    }
  };

  async #addGiftWrapping() {
    const body = JSON.stringify({
      items: [{ id: this.variantId, quantity: 1 }],
      sections: this.#sectionIds(),
      sections_url: window.location.pathname,
    });

    const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await res.json();

    if (!res.ok) {
      // /cart/add.js のエラー形式は { message, description } であって
      // .errors ではないので、res.ok で判定する
      throw new Error(data.description || data.message || 'Failed to add gift wrapping');
    }

    // /cart/add.js の成功レスポンスは追加した行の情報のみで、
    // currency などを含む完全なカート形状ではないため、
    // createCartFromAjaxResponse に渡す用に /cart.js を別途取得する
    const cartData = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());

    return { sectionsHtml: data.sections, cartData };
  }

  async #removeGiftWrapping() {
    const body = JSON.stringify({
      id: this.variantId,
      quantity: 0,
      sections: this.#sectionIds(),
      sections_url: window.location.pathname,
    });

    const res = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.description || data.message || 'Failed to remove gift wrapping');
    }

    // /cart/change.js は完全なカート形状を返すのでそのまま使える
    return { sectionsHtml: data.sections, cartData: data };
  }

  #sectionIds() {
    const ids = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  get variantId() {
    const { variantId } = this.dataset;
    if (!variantId) throw new Error('Variant id missing');
    return variantId;
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-gift-wrapping-component')) {
  customElements.define('cart-gift-wrapping-component', CartGiftWrappingComponent);
}